import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface ToilEntry {
  id: number;
  date: string;
  total_sum: number;
  source: 'report' | 'manual';
  description: string;
}

interface Props {
  userId: number | null;
  auth: boolean;
}

const ToilCard: React.FC<Props> = ({ userId, auth }) => {
  const [toils, setToils] = useState<ToilEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchToils = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await axios.get(`toils/user/${userId}`);
      setToils((data as ToilEntry[]).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (auth) fetchToils();
  }, [auth, fetchToils]);

  const balance = toils.reduce((sum, t) => sum + (t.total_sum || 0), 0);
  const recent = toils.slice(0, 5);

  const fmt = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

  return (
    <div
      className="dashboard-card p-[16px] flex flex-col"
    >
      <div className="flex items-center gap-[6px] mb-[10px]">
        <AccessTimeIcon style={{ fontSize: 15, color: '#4f8ef7' }} />
        <span className="text-[11px] font-semibold text-[var(--text-color-calendar-week)] uppercase tracking-wide">
          TOIL Balance
        </span>
      </div>

      {loading ? (
        <div className="flex flex-col gap-[8px]">
          <div className="skeleton-line" style={{ width: '55%', height: 36, marginBottom: 6 }} />
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-[8px]">
              <div className="skeleton-line" style={{ width: 36, height: 13 }} />
              <div className="skeleton-line" style={{ flex: 1, height: 13 }} />
              <div className="skeleton-line" style={{ width: 40, height: 13 }} />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div
            className="text-[36px] font-bold leading-none mb-[12px]"
            style={{ color: balance >= 0 ? '#43c98a' : '#ef4444' }}
          >
            {balance >= 0 ? '+' : ''}{Math.round(balance * 100) / 100}h
          </div>

          {recent.length === 0 ? (
            <div className="text-[12px] text-[var(--text-color-calendar-week)]">No records yet</div>
          ) : (
            <div className="flex flex-col gap-[4px] flex-1">
              {recent.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-[12px]">
                  <span className="text-[var(--text-color-calendar-week)]">{fmt(t.date)}</span>
                  <span style={{ color: t.total_sum >= 0 ? '#43c98a' : '#ef4444', fontWeight: 600 }}>
                    {t.total_sum >= 0 ? '+' : ''}{Math.round(t.total_sum * 100) / 100}h
                  </span>
                  <span
                    className="text-[10px] px-[5px] py-[1px] rounded-[3px]"
                    style={{
                      background: t.source === 'report' ? '#4f8ef720' : '#f4a94220',
                      color: t.source === 'report' ? '#4f8ef7' : '#f4a942',
                    }}
                  >
                    {t.source === 'report' ? 'auto' : 'manual'}
                  </span>
                </div>
              ))}
            </div>
          )}

          <Link
            to="/toil"
            className="mt-[12px] flex items-center gap-[4px] text-[11px] no-underline"
            style={{ color: '#4f8ef7' }}
          >
            All records <ArrowForwardIcon style={{ fontSize: 12 }} />
          </Link>
        </>
      )}
    </div>
  );
};

export default ToilCard;
